import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import InfoIcon from 'assets/info-icon-lusini.svg'
import Button from 'theme/atoms/Button'
import useAccount from 'hooks/account/useAccount'
import useTranslations from 'hooks/useTranslations'
import * as events from '../event'
import { isItemChannelRestricted } from 'hooks/account/utils'

type Props = {
  'data-cy-state'?: string
}

export default function ChannelInfoModal(props: Props) {
  const account = useAccount()
  const t = useTranslations<'templates-Cart'>()
  const evtSend = React.useRef(false)

  // https://jira.em-group.de/browse/WDV-2380
  React.useEffect(() => {
    if (account.isFetching) return
    if (account.cart.data.items.length === 0) return
    if (evtSend.current) return
    evtSend.current = true

    const skus: string[] = []
    const titles: string[] = []

    for (const item of account.cart.data.items) {
      if (!isItemChannelRestricted(item, account.channel)) return
      skus.push(item.sku)
      titles.push(item.title)
    }

    events.showChannelRestrictedModal(
      skus.join(', '),
      titles.join(', '),
      account.loggedIn
    )
  }, [
    account.cart.data.items,
    account.channel,
    account.loggedIn,
    account.isFetching
  ])

  const switchChannel = () => {
    account.setChannel(
      account.channel === 'b2b' ? 'b2c' : 'b2b',
      'cart_b2x_switch'
    )
    events.switchChannelByChannelModal()
  }

  const removeRestrictedItems = () => {
    account.cart.removeRestrictedItems()

    // // https://jira.em-group.de/browse/WDV-2380
    const skus: string[] = []
    const titles: string[] = []

    for (const item of account.cart.data.items) {
      if (!isItemChannelRestricted(item, account.channel)) return
      skus.push(item.sku)
      titles.push(item.title)
    }
    events.removeRestrictedItemsClick(skus.join(', '), titles.join(', '))
  }

  return (
    <Wrapper
      className="ChannelInfoModal"
      data-cy-collection="templates/Cart/ChannelInfoModal"
      data-cy-state={props['data-cy-state']}
    >
      <div className="svg-wrapper">
        <InfoIcon />
      </div>
      <div className="text-wrapper">
        <div className="description">
          {t(
            `channel_info_description_${
              account.channel === 'b2b' ? 'b2c' : 'b2b'
            }`
          )}
        </div>
        <div className="sub-headline">{t('channel_info_proceed')}</div>
        <div className="button-container">
          <Button
            variation="cart_disabled"
            data-cy-handle="remove-restricted-articles"
            onClick={removeRestrictedItems}
          >
            {t(
              `channel_info_remove_items_${
                account.channel === 'b2b' ? 'b2c' : 'b2b'
              }`
            )}
          </Button>

          {account.loggedIn && (
            <Button
              data-cy-state="show-contact-support-link"
              variation="cart_disabled"
              data-cy-handle="contact-support-link"
              onClick={events.contactSupportClick}
              to={t.asText(false, 'channel_info_support_link')}
            >
              {t('channel_info_support_text')}
            </Button>
          )}

          {!account.loggedIn && (
            <Button
              data-cy-state="show-channel-switch-button"
              variation="cart_disabled"
              data-cy-handle="b2x-switch"
              onClick={switchChannel}
            >
              {t(
                `channel_info_proceed_as_${
                  account.channel === 'b2b' ? 'b2c' : 'b2b'
                }`
              )}
            </Button>
          )}
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${theme.colors.accent.info};
  border: 1px solid ${theme.colors.accent.blue};
  padding: ${theme.spacing('xs')};
  display: flex;
  > .svg-wrapper {
    > svg {
      width: 1.5rem;
      margin-right: ${theme.spacing('xxs')};
      margin-top: -0.25rem;
      @media (min-width: ${theme.ms.SM}px) {
        margin-top: 0;
      }
    }
  }
  > .text-wrapper {
    > .description {
      margin-bottom: ${theme.spacing('m')};
      ${theme.ty('r-s', null, 'r-base')}
    }
    > .sub-headline {
      margin-bottom: ${theme.spacing('s')};
      ${theme.ty('rc-base', '700', 'rc-l')}
    }
    > .button-container {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing('xs')};
      @media (min-width: ${theme.ms.SM}px) {
        flex-direction: row;
      }
    }
  }
`
